import React from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import BlockRenderer from './BlockRenderer';
import OnchainBadges from './OnchainBadges';
import LeadCaptureForm from './LeadCaptureForm';
import ConnectButton from './ConnectButton';
import { FiGlobe } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const PublicProfileRenderer = ({ profile, blocks }) => {
  const { walletAddress } = useAuth();
  
  // Handle null profile
  if (!profile) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-dark-300">Loading profile...</p>
        </div>
      </div>
    );
  }
  
  const getThemeClasses = (theme) => {
    const themes = {
      'minimal-light': {
        bg: 'bg-gradient-to-br from-white via-gray-50 to-indigo-50',
        text: 'text-gray-900',
        subtext: 'text-gray-600',
        accent: 'text-indigo-600',
        border: 'border-gray-200',
        card: 'bg-white shadow-lg border border-gray-200',
        hover: 'hover:shadow-xl hover:border-indigo-300',
        button: 'bg-indigo-600 hover:bg-indigo-700 text-white',
        cover: 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500',
      },
      'dark-pro': {
        bg: 'bg-gradient-to-br from-dark-950 via-dark-900 to-dark-950',
        text: 'text-dark-100',
        subtext: 'text-dark-300',
        accent: 'text-orange-400',
        border: 'border-dark-700',
        card: 'glass-dark border border-dark-700',
        hover: 'hover:border-orange-500/50 hover:shadow-glow-orange-sm',
        button: 'glass-orange border border-orange-500/50 text-orange-400 hover:bg-orange-500/20',
        cover: 'bg-gradient-to-r from-dark-800 via-dark-700 to-orange-900/30',
      },
      'cyber-neon': {
        bg: 'bg-black',
        text: 'text-orange-400',
        subtext: 'text-orange-300',
        accent: 'text-cyan-400',
        border: 'border-orange-500',
        card: 'bg-black border-2 border-orange-500 shadow-[0_0_20px_rgba(255,91,19,0.3)]',
        hover: 'hover:shadow-[0_0_30px_rgba(255,91,19,0.5)] hover:border-cyan-400',
        button: 'bg-orange-500 text-black font-bold hover:bg-cyan-400 hover:shadow-[0_0_20px_rgba(6,182,212,0.8)]',
        cover: 'bg-gradient-to-r from-orange-500 via-cyan-500 to-purple-500',
      },
    };
    return themes[theme] || themes['dark-pro'];
  };

  const themeClasses = getThemeClasses(profile?.theme || 'dark-pro');

  // Filter and organize blocks by type
  const tipJarBlock = blocks.find(block => block.type === 'tipjar');
  const nftGalleryBlock = blocks.find(block => block.type === 'nftgallery');
  const badgesBlock = blocks.find(block => block.type === 'badges');
  const otherBlocks = blocks.filter(block => !['tipjar', 'nftgallery', 'badges'].includes(block.type));

  return (
    <div className={`min-h-screen ${themeClasses.bg} transition-colors duration-500`}>
      {/* Header */}
      <div className="relative">
        {/* Cover/Background */}
        <div className={`h-48 ${themeClasses.cover} ${themeClasses.border} border-b relative overflow-hidden`}>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1),transparent_50%)]"></div>
          </div>
        </div>

        {/* Profile Info */}
        <div className="relative px-4 pb-8">
          <div className="max-w-2xl mx-auto">
            {/* Avatar */}
            <div className="relative -mt-20 mb-6">
              {profile.avatar ? (
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ duration: 0.6, delay: 0.2, type: "spring" }}
                  className="relative mx-auto w-32 h-32"
                >
                  <div className={`absolute inset-0 rounded-full ${themeClasses.accent.replace('text-', 'bg-')} opacity-20 blur-xl`}></div>
                  <img
                    src={profile.avatar}
                    alt={profile.display_name || profile.username}
                    className="relative w-32 h-32 rounded-full border-4 border-white shadow-2xl object-cover mx-auto"
                  />
                </motion.div>
              ) : (
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ duration: 0.6, delay: 0.2, type: "spring" }}
                  className={`w-32 h-32 rounded-full border-4 border-white shadow-2xl mx-auto flex items-center justify-center ${themeClasses.card} relative`}
                >
                  <div className={`absolute inset-0 rounded-full ${themeClasses.accent.replace('text-', 'bg-')} opacity-20 blur-xl`}></div>
                  <SafeIcon icon={FiGlobe} className={`text-4xl ${themeClasses.accent} relative z-10`} />
                </motion.div>
              )}
            </div>

            {/* Name & Bio */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center mb-8"
            >
              <h1 className={`text-2xl font-bold ${themeClasses.text} mb-2`}>
                {profile.display_name || profile.username}
              </h1>
              {profile.display_name && (
                <p className={`${themeClasses.subtext} mb-2`}>
                  @{profile.username}
                </p>
              )}
              {profile.bio && (
                <p className={`${themeClasses.subtext} max-w-md mx-auto leading-relaxed`}>
                  {profile.bio}
                </p>
              )}

              {/* Wallet Address */}
              {profile.wallet_address && (
                <div className={`inline-flex items-center mt-4 px-3 py-1 rounded-full text-sm ${themeClasses.border} border ${themeClasses.subtext}`}>
                  <SafeIcon icon={FiGlobe} className="mr-2" />
                  {profile.wallet_address.slice(0, 6)}...{profile.wallet_address.slice(-4)}
                </div>
              )}

              {/* Connect Button */}
              <div className="mt-4">
                <ConnectButton 
                  targetProfile={profile} 
                  targetWallet={profile.wallet_address}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Content Blocks */}
      <div className="px-4 pb-12">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Regular Content Blocks */}
          {otherBlocks.map((block, index) => (
            <motion.div
              key={block.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 + (index * 0.1) }}
            >
              <BlockRenderer block={block} theme={themeClasses} profile={profile} />
            </motion.div>
          ))}
          
          {/* Tip Jar */}
          {tipJarBlock && tipJarBlock.data.enabled && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <BlockRenderer block={tipJarBlock} theme={themeClasses} profile={profile} />
            </motion.div>
          )}

          {/* NFT Gallery */}
          {nftGalleryBlock && nftGalleryBlock.data.enabled && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <BlockRenderer block={nftGalleryBlock} theme={themeClasses} profile={profile} />
            </motion.div>
          )}

          {/* Onchain Badges */}
          {profile.wallet_address && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <OnchainBadges profile={profile} theme={themeClasses} />
            </motion.div>
          )}

          {/* Lead Capture Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <LeadCaptureForm 
              profileId={profile.id} 
              profileUsername={profile.username}
            />
          </motion.div>

          {blocks.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className={`text-center py-12 ${themeClasses.subtext}`}
            >
              <SafeIcon icon={FiGlobe} className="text-4xl mb-4 mx-auto opacity-50" />
              <p>No content blocks yet</p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className={`${themeClasses.border} border-t py-8`}>
        <div className="max-w-2xl mx-auto px-4 text-center">
          <p className={`${themeClasses.subtext} text-sm`}>
            Powered by <span className={themeClasses.accent}>HubID</span> - Web3 Digital Identity
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicProfileRenderer;